import React from 'react'

import type { FC } from 'react'

import { cn } from '@/lib/utils'

import CustomFilter from '../CustomFilter'
import GroupFilter from '../GroupFilter'
import HideColumnFilter from '../HideColumnFilter'
import SearchInput from '../SearchFilter'
import type { FilterShape } from './filter-shapes'

type ContainerProps = {
	matchDownMD: boolean
	hasChildren?: boolean
	className?: string
	children: React.ReactNode
}

function Container({
	searchOnly,
	children,
}: {
	searchOnly: boolean
	children: React.ReactNode
}) {
	return (
		<div
			data-no-print
			className={cn(
				'flex flex-row items-center flex-wrap bg-background h-auto w-full p-4',
				searchOnly ? 'gap-0' : 'gap-4'
			)}
		>
			{children}
		</div>
	)
}

function SearchContainer({
	matchDownMD,
	hasChildren,
	children,
}: ContainerProps) {
	return (
		<div
			className={cn(
				'flex order-0',
				matchDownMD && hasChildren ? 'w-1/2' : 'w-auto'
			)}
		>
			{children}
		</div>
	)
}

function ToolbarContainer({
	matchDownMD,
	hasChildren,
	children,
}: ContainerProps) {
	return (
		<div
			className={cn(
				'flex flex-row justify-start items-center flex-wrap gap-4',
				matchDownMD && hasChildren ? 'w-full' : 'w-auto',
				matchDownMD ? 'order-2' : 'order-1'
			)}
		>
			{children}
		</div>
	)
}

function ChildrenContainer({
	matchDownMD,
	hasChildren,
	children,
}: ContainerProps) {
	return (
		<div
			className={cn(
				'flex justify-end',
				matchDownMD && hasChildren ? 'w-1/2' : 'w-auto',
				matchDownMD ? 'order-1' : 'order-2',
				hasChildren && 'flex-1'
			)}
		>
			{children}
		</div>
	)
}

export interface TableFiltersProps {
	filters: FilterShape[]
	actions?: React.ReactNode[]
}

export const TableFilters: FC<React.PropsWithChildren<TableFiltersProps>> = ({
	filters,
	actions,
	children,
}) => {
	// Custom hook for media query - you can replace this with your own implementation
	// or use a library like react-responsive
	const [matchDownMD, setMatchDownMD] = React.useState(false)

	React.useEffect(() => {
		const mediaQuery = window.matchMedia('(max-width: 768px)')

		const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
			setMatchDownMD(e.matches)
		}

		// Set initial value
		handleChange(mediaQuery)

		// Add listener
		mediaQuery.addEventListener('change', handleChange)

		return () => {
			mediaQuery.removeEventListener('change', handleChange)
		}
	}, [])

	const searchFilter = filters.find(filter => filter.type === 'search')
	const searchOnly = searchFilter && filters.length === 1 && !actions

	return (
		<Container searchOnly={searchOnly}>
			{searchFilter && (
				<SearchContainer
					matchDownMD={matchDownMD}
					hasChildren={!!children}
				>
					<SearchInput
						key={searchFilter.key || searchFilter.toString()}
						value={searchFilter.value}
						onChange={searchFilter.onChange}
						operator={searchFilter.operator}
					/>
				</SearchContainer>
			)}

			<ChildrenContainer
				matchDownMD={matchDownMD}
				hasChildren={!!children}
			>
				{children}
			</ChildrenContainer>

			<ToolbarContainer
				matchDownMD={matchDownMD}
				hasChildren={!!children}
			>
				{filters.map(filter => {
					switch (filter.type) {
						case 'custom':
							return (
								<CustomFilter
									key={filter.key}
									options={filter.options}
									setTableFilters={filter.onChange}
									value={filter.value}
								/>
							)

						case 'group':
							return (
								<GroupFilter
									key={filter.key}
									options={filter.options}
									onChange={filter.onChange}
									value={filter.value}
								/>
							)

						case 'hide-columns':
							return (
								<HideColumnFilter
									key={filter.key}
									onChange={filter.onChange}
									value={filter.value}
									options={filter.options}
								/>
							)

						default:
							return null
					}
				})}
				<div className="flex gap-4">
					{actions?.map((action, index) => (
						<React.Fragment key={index}>{action}</React.Fragment>
					))}
				</div>
			</ToolbarContainer>
		</Container>
	)
}
